import { observable, action, makeObservable } from 'mobx'
import get from 'lodash/get'
import {
  ITEM_TYPE_MAIL,
  ITEM_TYPE_RESPONDER,
} from '@client/components/Editor/settings'
import * as sentry from '@client/utils/sentry'

/**
 * @type {string} Key used to save item data in local storage.
 */
export const KEY_DEFAULT_NAME = 'gan-editor-auto-save'
export const KEY_CANCEL_NAME = 'gan-editor-cancel-save'
export const MAX_MAILS_TO_SAVE_PER_USER = 5
export const MAX_MAILS_TO_SAVE_PER_USER_CANCEL = 1
export const MAX_SAVE_ATTEMPTS_PER_KEY = 10
// Hard cap on total queued autosave entries across all users on this browser.
// Without this, each previously-logged-in account leaves up to MAX_MAILS_TO_SAVE_PER_USER
// stale entries that accumulate forever and eventually fill the localStorage quota.
export const MAX_TOTAL_QUEUE_SIZE = 20

/**
 * Work with browsers local storage
 * @class
 * @name Storage
 */
export class Storage {
  constructor(key = KEY_DEFAULT_NAME) {
    this.key = key
    this.userId = undefined
    this.itemId = undefined
    this.kind = undefined
    this.maxMailsPerUser = key === KEY_DEFAULT_NAME
      ? MAX_MAILS_TO_SAVE_PER_USER
      : MAX_MAILS_TO_SAVE_PER_USER_CANCEL
    this.saveLimits = {}
    makeObservable(this)
    this.queue = this.initQueue()
  }

  @observable disabled = false

  @action setDisabled = (value) => {
    this.disabled = value
  }

  initQueue() {
    this.saveLimits = {}
    if (
      typeof window === 'undefined' ||
      typeof window.localStorage === 'undefined'
    ) {
      return []
    }

    let queue = window.localStorage.getItem(this.getQueueKey())

    const storageKeys = this.getStorageKeys()
    if (queue) {
      try {
        let parsedQueue = JSON.parse(queue)
        if (!Array.isArray(parsedQueue)) {
          parsedQueue = []
        }
        queue = [
          ...parsedQueue.filter((key) => storageKeys.indexOf(key) !== -1),
          ...storageKeys.filter((key) => parsedQueue.indexOf(key) === -1),
        ]
      } catch (error) {
        console.warn(
          'Init queue from storage failed. Check JSON format.\n' + error
        )
        queue = storageKeys
      }
    } else {
      queue = storageKeys
    }

    return this.trimToTotalLimit(queue)
  }

  // Cap total queue length so stale entries from previously-logged-in users
  // can't grow without bound. Drops the oldest (queue tail) and removes them
  // from localStorage so the bytes are actually freed.
  trimToTotalLimit(queue) {
    if (!Array.isArray(queue) || queue.length <= MAX_TOTAL_QUEUE_SIZE) {
      return queue || []
    }
    const kept = queue.slice(0, MAX_TOTAL_QUEUE_SIZE)
    const dropped = queue.slice(MAX_TOTAL_QUEUE_SIZE)
    dropped.forEach((key) => {
      try {
        localStorage.removeItem(key)
      } catch (_) {
        // removeItem doesn't throw under normal conditions; ignore defensively.
      }
    })
    return kept
  }

  getStorageKeys() {
    return Object.keys(localStorage).filter(
      (key) => `${key}`.startsWith(this.key) && `${key}` !== this.getQueueKey()
    )
  }

  setUserId(userId) {
    this.userId = userId
  }

  setItemId(itemId) {
    this.itemId = itemId
  }

  setItemType(type) {
    this.kind = type
  }

  setMaxMailsPerUser(maxMailsPerUser) {
    this.maxMailsPerUser = maxMailsPerUser
  }

  getKey() {
    return `${this.key}_${this.userId}_${this.itemId}${
      this.kind &&
      this.kind !== ITEM_TYPE_MAIL &&
      this.kind !== ITEM_TYPE_RESPONDER
        ? `_${this.kind}`
        : ''
    }`
  }

  getQueueKey() {
    return `${this.key}_queue`
  }

  removeExtraKeys(queue) {
    const keysPerUser = {}
    return queue
      .map((key) => ({
        key,
        parts: `${key}`.split('_'),
      }))
      .filter((item) => {
        const userId = get(item, 'parts[1]')
        const key = get(item, 'key')
        if (!userId || !key) {
          return
        }
        if (!keysPerUser[userId]) {
          keysPerUser[userId] = []
        }
        if (keysPerUser[userId].length >= this.maxMailsPerUser) {
          localStorage.removeItem(key)
          return false
        }
        keysPerUser[userId].push(key)
        return item
      })
      .map((item) => item.key)
  }

  /**
   * Saves item in local storage
   * @param {json} item - Item data to be saved
   */
  saveItem(item) {
    if (this.disabled) {
      return
    }

    let result = null

    if (get(item, 'id')) {
      this.setItemId(item.id)
    }
    if (get(item, 'hash')) {
      this.setItemId(item.hash)
    }

    try {
      result = JSON.stringify(item)
    } catch (error) {
      console.warn(
        `Auto saving data with keyName "${this.getKey()}" failed. Check JSON format! \n ${error}`
      )
    }

    if (result && this.itemId && this.userId) {
      try {
        localStorage.setItem(this.getKey(), result)
        this.queue = [
          this.getKey(),
          ...this.queue.filter((key) => key !== this.getKey()),
        ]
        this.queue = this.removeExtraKeys(this.queue)
        this.queue = this.trimToTotalLimit(this.queue)
        localStorage.setItem(this.getQueueKey(), JSON.stringify(this.queue))
        this.saveLimits[this.getKey()] = 0
      } catch (e) {
        const oldestKey = this.queue.shift()
        if (oldestKey) {
          localStorage.removeItem(oldestKey)
          try {
            localStorage.setItem(this.getQueueKey(), JSON.stringify(this.queue))
          } catch (_) {
            // Storage still full after eviction; queue metadata update skipped
          }
        }

        if ((this.saveLimits[this.getKey()] || 1) < MAX_SAVE_ATTEMPTS_PER_KEY) {
          this.saveLimits[this.getKey()] = (this.saveLimits[this.getKey()] || 1) + 1
          return this.saveItem(item)
        } else if ((this.saveLimits[this.getKey()] || 1) === MAX_SAVE_ATTEMPTS_PER_KEY) {
          this.setDisabled(true)
          sentry.captureException(e)
        }
      }
    }
  }

  /**
   * Reads item from local storage
   * @return {json} Item's data
   */
  getItem() {
    let result = localStorage.getItem(this.getKey())

    if (result) {
      try {
        result = JSON.parse(result)
      } catch (error) {
        console.warn(
          'Getting item from storage failed. Check JSON format.\n' + error
        )
        result = {}
      }
    } else {
      result = {}
    }

    return result
  }

  clear() {
    localStorage.removeItem(this.getKey())
    this.queue = [...this.queue.filter((key) => key !== this.getKey())]
    localStorage.setItem(this.getQueueKey(), JSON.stringify(this.queue))
    this.saveLimits = {}
    this.setDisabled(false)
  }

  // Wipe every entry this Storage instance owns (across all users on this
  // browser). Intended as the user-facing recovery for "localStorage full":
  // we drop accumulated drafts, reset retry/disabled state, and let auto-save
  // resume. The current in-memory edit is preserved by the EditorModel and
  // re-saved on the next autosave tick.
  @action purgeAll = () => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return
    }
    const keys = Object.keys(localStorage).filter((k) => `${k}`.startsWith(this.key))
    keys.forEach((k) => {
      try {
        localStorage.removeItem(k)
      } catch (_) {
        // ignore
      }
    })
    this.queue = []
    this.saveLimits = {}
    this.disabled = false
  }
}

export const cancelStorage = new Storage(KEY_CANCEL_NAME)

const defaultStorage = new Storage(KEY_DEFAULT_NAME)

// Recover from a "localStorage full" state by wiping both auto-save and
// cancel-save data, so further saves can succeed.
export const purgeAllAutoSave = () => {
  defaultStorage.purgeAll()
  cancelStorage.purgeAll()
}

export default defaultStorage
